import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(!!localStorage.getItem("sb_admin_token"));

  useEffect(() => {
    const token = localStorage.getItem("sb_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => {
        localStorage.removeItem("sb_token");
        setUser(false);
      })
      .finally(() => setLoading(false));
  }, []);

  const persist = (data) => {
    localStorage.setItem("sb_token", data.token);
    setUser(data.user);
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    persist(data);
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    persist(data);
  };

  const requestPin = (email, purpose) => api.post("/auth/request-pin", { email, purpose });
  const verifyPin = (email, purpose, pin) => api.post("/auth/verify-pin", { email, purpose, pin }).then((r) => r.data.verification_token);

  const completeSignup = async (payload) => {
    const { data } = await api.post("/auth/complete-signup", payload);
    persist(data);
    return data; // { token, user, checkout_plan }
  };

  const resetPasswordWithToken = async (verification_token, newPassword) => {
    const { data } = await api.post("/auth/reset-password", { verification_token, new_password: newPassword });
    persist(data);
  };

  const logout = () => {
    localStorage.removeItem("sb_token");
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  };

  const impersonate = async (token) => {
    const admin = localStorage.getItem("sb_token");
    if (admin && !localStorage.getItem("sb_admin_token")) localStorage.setItem("sb_admin_token", admin);
    localStorage.setItem("sb_token", token);
    setImpersonating(true);
    await refreshUser();
  };

  const stopImpersonate = async () => {
    const admin = localStorage.getItem("sb_admin_token");
    if (admin) { localStorage.setItem("sb_token", admin); localStorage.removeItem("sb_admin_token"); }
    setImpersonating(false);
    await refreshUser();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, requestPin, verifyPin, completeSignup, resetPasswordWithToken, logout, refreshUser, impersonating, impersonate, stopImpersonate }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
